/**
 * Version history with restore.
 *
 * Restoring is a deliberate, confirmed action, and the confirmation says what will actually happen:
 * the record returns to draft so it must be re-approved, and the URL is preserved so inbound links
 * keep working.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Modal, Spinner } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { cmsApi } from '@/services/api';
import { ApiError } from '@/services/client';
import type { Revision } from '@/services/types';

export function RevisionHistory({
  resource,
  slug,
  onRestored,
}: {
  resource: string;
  slug: string;
  onRestored?: () => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Revision | null>(null);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['cms', resource, slug, 'revisions'],
    queryFn: () => cmsApi.revisions(resource, slug),
  });

  const restoreMutation = useMutation({
    mutationFn: (version: number) => cmsApi.restoreRevision(resource, slug, version, 'Restored from the CMS'),
    onSuccess: () => {
      setPending(null);
      setError('');
      void queryClient.invalidateQueries({ queryKey: ['cms', resource] });
      onRestored?.();
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : 'The restore failed.');
    },
  });

  const revisions = data?.results ?? [];

  return (
    <Card>
      <h2 className="text-base font-semibold text-ink">Version history</h2>
      <p className="mt-1 text-xs text-muted">Every save and status change is recorded.</p>

      {isLoading ? (
        <div className="mt-4">
          <Spinner label="Loading history" />
        </div>
      ) : revisions.length === 0 ? (
        <p className="mt-4 text-sm text-muted">No versions recorded yet.</p>
      ) : (
        <ol className="mt-4 max-h-80 space-y-3 overflow-y-auto pr-1">
          {revisions.map((revision) => (
            <li key={revision.id} className="border-b border-line pb-3 last:border-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">
                    v{revision.version}
                    {revision.is_autosave && <span className="ml-2 text-xs font-normal text-muted">autosave</span>}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted">{revision.comment || 'No comment'}</p>
                  <p className="mt-0.5 text-xs text-muted">
                    {revision.created_by_name} ·{' '}
                    {new Date(revision.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </div>
                {revision.status_at_snapshot && <Badge tone="neutral">{revision.status_at_snapshot}</Badge>}
              </div>
              {can('can_publish') && (
                <button
                  type="button"
                  onClick={() => setPending(revision)}
                  className="mt-2 text-xs font-semibold text-primary underline"
                >
                  Restore this version
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      <Modal
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        title={`Restore version ${pending?.version ?? ''}?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => pending && restoreMutation.mutate(pending.version)}
              loading={restoreMutation.isPending}
            >
              Restore version {pending?.version}
            </Button>
          </>
        }
      >
        {error && (
          <Alert tone="danger" className="mb-4">
            {error}
          </Alert>
        )}
        <p className="text-sm leading-relaxed text-ink-soft">
          The record's content will be replaced with the values saved in version {pending?.version}. Two things stay as
          they are:
        </p>
        <ul className="mt-3 space-y-2 text-sm text-ink-soft">
          <li>
            <strong className="text-ink">The URL is preserved</strong> — inbound links and search rankings are not
            broken.
          </li>
          <li>
            <strong className="text-ink">The record returns to draft</strong> — restored content is re-approved before it
            goes live again.
          </li>
        </ul>
        <p className="mt-3 text-sm text-muted">
          The current state is snapshotted first, so this restore is itself reversible.
        </p>
      </Modal>
    </Card>
  );
}
